import { createSlice } from '@reduxjs/toolkit';
import { loadProjectRecordTotalHours } from 'store/records/thunks/loadProjectRecordTotalHours';
import { loadClockedInPeople } from './thunks/loadClockedInPeople.thunk';
import { loadMyDayRecords } from './thunks/loadMyDayRecords.thunk';
import { loadProjectExpenseSummary } from './thunks/loadProjectExpenseSummary.thunk';
import { loadProjectFormAttention } from './thunks/loadProjectFormAttention.thunk';
import { loadProjectWeather } from './thunks/loadProjectWeather.thunk';

/**
 * The type of deleteRecordThunk's fulfilled action
 * (store/records/thunks/deleteRecordThunk), its payload the records the API
 * moved away: the one deleted and its breaks. Matched by its type, as the
 * thunk would bring the records' and the permissions' modules into this
 * slice.
 */
export const RECORD_DELETED = 'recordDialog/deleteRecord/fulfilled';

/**
 * State of the redesigned project overview (the project dashboard's top card)
 * that outlives a visit:
 * - `expenseSummaries`: the expense card's computed summaries, by project,
 *   so revisiting a project within EXPENSE_SUMMARY_MAX_AGE_MS shows them at
 *   once. Each entry: `{ status: 'loading'|'succeeded'|'failed', summary,
 *   loadedAt, error }` — a reload keeps the previous summary on screen.
 *   `summary` is the thunk's payload but `loadedAt`: the figures and the
 *   dashboard's chart data, `series` and `topCategories`.
 * - `formAttention.nonConformance` / `formAttention.trMeters`: the
 *   Poikkeamaraportit and TR-mittarit lists, by project. Each entry:
 *   `{ status, data, noAccess, error }`, `data` the API payload (null with
 *   `noAccess`); a reload keeps the previous list on screen.
 * - `weather`: the current weather at each project's site. Each entry:
 *   `{ status, current, today, loadedAt, error }` (`current` null without a
 *   location; `today` the day's forecast hours and lowest / highest
 *   temperature, loadProjectWeather's weatherTodayOf, null without one); a
 *   reload keeps the previous reading on screen.
 * - `hourTotalsLoadedAt`: when each project's record hour totals (the
 *   projects slice's `projectRecordHours`) last came in, epoch ms: the
 *   records clocked out after it are not in them, and "Työtunnit tänään"
 *   times those itself.
 * - `myDayRecords`: the signed-in user's own records, every project's, from
 *   a day before the start of this week or month to the end of today, for
 *   "Oma päiväni" (loadMyDayRecords): `{ status, records, fromDate, loadedAt,
 *   error }`, null before the first load; a reload keeps the previous records
 *   on screen. A record deleted on the page (deleteRecordThunk, which also
 *   takes its breaks) leaves them, as it leaves the page's records.
 * - `clockedInPeople`: who is clocked in to each project right now, for the
 *   dashboard's phone list (loadClockedInPeople). Each entry: `{ status,
 *   people, loadedAt, error }`, `people` null before the first load; a
 *   reload keeps the previous list on screen. Read through
 *   selectors/clockedInPeople.selectors, which stays clear of this file's
 *   imports.
 */
const projectOverviewSlice = createSlice({
  name: 'projectOverview',
  initialState: {
    expenseSummaries: {},
    formAttention: {
      nonConformance: {},
      trMeters: {},
    },
    weather: {},
    hourTotalsLoadedAt: {},
    myDayRecords: null,
    clockedInPeople: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadProjectExpenseSummary.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.expenseSummaries[projectId] = {
          summary: null,
          loadedAt: null,
          ...state.expenseSummaries[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadProjectExpenseSummary.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        const { loadedAt, ...summary } = action.payload;
        state.expenseSummaries[projectId] = {
          status: 'succeeded',
          summary,
          loadedAt,
          error: null,
        };
      })
      .addCase(loadProjectExpenseSummary.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.expenseSummaries[projectId] = {
          summary: null,
          loadedAt: null,
          ...state.expenseSummaries[projectId],
          status: 'failed',
          error: action.error?.message || 'Failed to load the expense summary',
        };
      })
      .addCase(loadProjectFormAttention.pending, (state, action) => {
        const { projectId, kind } = action.meta.arg;
        const lists = (state.formAttention[kind] =
          state.formAttention[kind] || {});
        lists[projectId] = {
          data: null,
          noAccess: false,
          ...lists[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadProjectFormAttention.fulfilled, (state, action) => {
        const { projectId, kind } = action.meta.arg;
        const { noAccess, ...data } = action.payload || {};
        state.formAttention[kind][projectId] = {
          status: 'succeeded',
          data: noAccess ? null : data,
          noAccess: Boolean(noAccess),
          error: null,
        };
      })
      .addCase(loadProjectFormAttention.rejected, (state, action) => {
        const { projectId, kind } = action.meta.arg;
        const lists = state.formAttention[kind];
        lists[projectId] = {
          data: null,
          noAccess: false,
          ...lists[projectId],
          status: 'failed',
          error: action.error?.message || 'Failed to load the forms',
        };
      })
      .addCase(loadProjectWeather.pending, (state, action) => {
        const projectId = action.meta.arg.project.id;
        state.weather[projectId] = {
          current: null,
          loadedAt: null,
          ...state.weather[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadProjectWeather.fulfilled, (state, action) => {
        const projectId = action.meta.arg.project.id;
        state.weather[projectId] = {
          status: 'succeeded',
          current: action.payload.current,
          today: action.payload.today ?? null,
          loadedAt: action.payload.loadedAt,
          error: null,
        };
      })
      .addCase(loadProjectWeather.rejected, (state, action) => {
        const projectId = action.meta.arg.project.id;
        state.weather[projectId] = {
          current: null,
          loadedAt: null,
          ...state.weather[projectId],
          status: 'failed',
          error: action.error?.message || 'Failed to load the weather',
        };
      })
      .addCase(loadProjectRecordTotalHours.fulfilled, (state, action) => {
        const { id, loadedAt } = action.payload || {};
        if (id && loadedAt) {
          state.hourTotalsLoadedAt[id] = loadedAt;
        }
      })
      .addCase(loadMyDayRecords.pending, (state) => {
        state.myDayRecords = {
          records: [],
          fromDate: null,
          loadedAt: null,
          ...state.myDayRecords,
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadMyDayRecords.fulfilled, (state, action) => {
        const { records, fromDate, loadedAt } = action.payload;
        state.myDayRecords = {
          status: 'succeeded',
          records,
          fromDate,
          loadedAt,
          error: null,
        };
      })
      .addCase(loadMyDayRecords.rejected, (state, action) => {
        state.myDayRecords = {
          records: [],
          fromDate: null,
          loadedAt: null,
          ...state.myDayRecords,
          status: 'failed',
          error: action.error?.message || 'Failed to load your records',
        };
      })
      .addCase(loadClockedInPeople.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.clockedInPeople[projectId] = {
          people: null,
          loadedAt: null,
          ...state.clockedInPeople[projectId],
          status: 'loading',
          error: null,
        };
      })
      .addCase(loadClockedInPeople.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        state.clockedInPeople[projectId] = {
          status: 'succeeded',
          people: action.payload.people,
          loadedAt: action.payload.loadedAt,
          error: null,
        };
      })
      .addCase(loadClockedInPeople.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.clockedInPeople[projectId] = {
          people: null,
          loadedAt: null,
          ...state.clockedInPeople[projectId],
          status: 'failed',
          error:
            action.error?.message || 'Failed to load the clocked-in people',
        };
      })
      .addCase(RECORD_DELETED, (state, action) => {
        if (!state.myDayRecords?.records?.length) return;
        // The deleted records: the one deleted and its breaks.
        const deletedIds = new Set(
          (Array.isArray(action.payload) ? action.payload : [])
            .map((record) => record?.id)
            .filter(Boolean)
        );
        state.myDayRecords.records = state.myDayRecords.records.filter(
          (record) => !deletedIds.has(record.id)
        );
      });
  },
});

/** The expense card's entry of a project, or null before its first load. */
export const selectProjectExpenseSummaryEntry = (state, projectId) =>
  state.projectOverview?.expenseSummaries?.[projectId] || null;

/**
 * A project's Poikkeamaraportit (`kind` 'nonConformance') or TR-mittarit
 * ('trMeters') list entry, or null before its first load.
 */
export const selectProjectFormAttentionEntry = (state, kind, projectId) =>
  state.projectOverview?.formAttention?.[kind]?.[projectId] || null;

/** The current weather entry of a project's site, or null before its load. */
export const selectProjectWeatherEntry = (state, projectId) =>
  state.projectOverview?.weather?.[projectId] || null;

/**
 * When a project's record hour totals last came in (epoch ms), or null
 * before they have.
 */
export const selectProjectHourTotalsLoadedAt = (state, projectId) =>
  state.projectOverview?.hourTotalsLoadedAt?.[projectId] || null;

/**
 * The entry of the user's own records for "Oma päiväni", or null before its
 * first load.
 */
export const selectMyDayRecordsEntry = (state) =>
  state.projectOverview?.myDayRecords || null;

export default projectOverviewSlice.reducer;
